<template>
  <div class="container-fluid py-4">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div class="d-flex align-items-center">
        <i class="fas fa-box text-primary me-2"></i>
        <h2 class="mb-0">{{ $t('product.products') }}</h2>
      </div>
      <div class="d-flex align-items-center gap-2">
        <button class="btn btn-outline-primary btn-sm" :class="{ active: activeFilter === 'all' }" @click="setFilter('all')">
          {{ $t('product.allProducts') }}
        </button>
        <button class="btn btn-outline-primary btn-sm" :class="{ active: activeFilter === 'ddr' }" @click="setFilter('ddr')">
          {{ $t('product.ddrMemory') }}
        </button>
        <button class="btn btn-outline-primary btn-sm" :class="{ active: activeFilter === 'ssd' }" @click="setFilter('ssd')">
          {{ $t('product.ssdStorage') }}
        </button>
        <div class="dropdown">
          <button class="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" data-bs-toggle="dropdown">
            <i class="fas fa-sort me-1"></i>{{ $t('product.sortBy') }}
          </button>
          <ul class="dropdown-menu">
            <li><a class="dropdown-item" href="#" @click.prevent="setSortBy('name')">{{ $t('product.name') }}</a></li>
            <li><a class="dropdown-item" href="#" @click.prevent="setSortBy('price')">{{ $t('product.price') }}</a></li>
            <li><a class="dropdown-item" href="#" @click.prevent="setSortBy('date')">{{ $t('product.dateAdded') }}</a></li>
            <li><a class="dropdown-item" href="#" @click.prevent="setSortBy('popularity')">{{ $t('product.popularity') }}</a></li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="row mb-4">
      <div class="col-md-6">
        <div class="input-group">
          <span class="input-group-text">
            <i class="fas fa-search"></i>
          </span>
          <input type="text" v-model="searchQuery" @input="handleSearch" class="form-control" :placeholder="$t('product.searchProducts')">
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center">
        <div class="spinner-border" role="status">
            <span class="visually-hidden">{{ $t('product.loading') }}</span>
        </div>
    </div>

    <div v-else class="row">
      <div v-for="product in products" :key="product.id" class="col-lg-4 col-md-6 mb-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body">
            <h5 class="card-title mb-3">
              <a 
                v-if="product.url" 
                :href="product.url" 
                target="_blank" 
                rel="noopener noreferrer" 
                class="text-decoration-none text-dark product-title"
                @click="handleTitleClick(product)"
              >
                {{ product.name || 'Mini Gaming PC, 12Th Gen Core i9-12900H Up to 5 GHz, RTX 3050 Ti 8GB GDDR6 Mini Desktop Computer, 32GB RAM 1TB NVMe SSD, Support RGB Lights, 4K Triple Display, 2.5G LAN, Wi-Fi 6E BT 5.3, Win 11 Pro' }}
                <i class="fas fa-external-link-alt ms-1 text-muted" style="font-size: 0.8em;"></i>
              </a>
              <router-link 
                v-else 
                :to="`/products/${product.id}`" 
                class="text-decoration-none text-dark product-title"
              >
                {{ product.name || 'Mini Gaming PC, 12Th Gen Core i9-12900H Up to 5 GHz, RTX 3050 Ti 8GB GDDR6 Mini Desktop Computer, 32GB RAM 1TB NVMe SSD, Support RGB Lights, 4K Triple Display, 2.5G LAN, Wi-Fi 6E BT 5.3, Win 11 Pro' }}
              </router-link>
            </h5>
            
            <!-- Product Tags -->
            <div class="mb-3">
              <span class="badge me-1" :class="product.category === 'DDR' ? 'bg-info' : product.category === 'SSD' ? 'bg-success' : 'bg-secondary'">
                {{ product.category || 'DDR' }}
              </span>
              <span class="badge bg-light text-dark">{{ product.brand || 'Unknown' }}</span>
            </div>
            
            <!-- Purchase Count Badge -->
            <div v-if="product.bought_past_month" class="mb-2">
              <span class="badge bg-warning text-dark">
                <i class="fas fa-fire me-1"></i>
                {{ formatBoughtCount(product.bought_past_month) }} {{ $t('product.monthlyPurchases') }}
              </span>
            </div>
            
            <!-- Price Information -->
            <div class="mb-3">
              <div class="mb-3">
              <div v-if="product.price !== null" class="price-info">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="fw-bold text-primary">{{ product.currency }} {{ formatPrice(product.price) }}</span>
                  <span v-if="product.is_adata_product" class="ms-2 text-muted small">
                    (BSP: {{ product.currency }} {{ formatPrice(product.bsp_price) }}
                    <button class="btn btn-sm btn-outline-secondary ms-1" @click="editBspPrice(product)" style="padding: 0.1rem 0.3rem; font-size: 0.7em;">
                      <i class="fas fa-edit"></i>
                    </button>)
                  </span>
                  <span v-if="product.price_trend" class="badge" :class="{'bg-success': product.price_trend === 'up', 'bg-danger': product.price_trend === 'down', 'bg-secondary': product.price_trend === 'stable'}">
                    {{ product.price_trend }}
                  </span>
                </div>
              </div>
              <div v-else class="text-center py-2">
                <i class="fas fa-exclamation-triangle text-warning me-1"></i>
                <span class="text-muted small">{{ $t('product.priceDataNotAvailable') }}</span>
              </div>
            </div>
            </div>
            
            <!-- Product Actions -->
            <div class="d-flex gap-2 mb-3">
              <!-- View Details -->
              <button class="btn btn-primary btn-sm flex-fill" @click="showProductDetails(product)">
                <i class="fas fa-eye me-1"></i>{{ $t('product.details') }}
              </button>
              
              <!-- Price History -->
              <button class="btn btn-outline-info btn-sm flex-fill" @click="showPriceHistory(product)">
                <i class="fas fa-chart-line me-1"></i>{{ $t('product.history') }}
              </button>
              
              <!-- Reviews -->
              <button class="btn btn-outline-success btn-sm flex-fill" @click="showReviews(product)">
                <i class="fas fa-star me-1"></i>{{ $t('product.reviews') }}
                <span v-if="product.review_count" class="badge bg-success ms-1">{{ product.review_count }}</span>
              </button>
            </div>
            
            <!-- Date Added -->
            <div class="text-muted small mb-2">
              <i class="fas fa-clock me-1"></i>
              {{ $t('product.added') }} {{ product.created_at ? formatDate(product.created_at) : '2025-07-26' }}
            </div>

            <!-- Comments -->
            <div class="mb-3 d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ $t('product.comments') }}:</strong>
                <p class="card-text small text-muted mb-0">{{ product.comments || $t('product.noComments') }}</p>
              </div>
              <button class="btn btn-outline-secondary btn-sm" @click="showEditCommentsModal(product)">
                <i class="fas fa-edit"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="!products.length" class="col-12">
        <div class="text-center py-5">
          <i class="fas fa-box-open fa-4x text-muted opacity-50 mb-3"></i>
          <h4 class="text-muted">{{ $t('product.noProductsFound') }}</h4>
          <p class="text-muted">{{ $t('product.tryAdjustingSearch') }}</p>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <nav aria-label="Page navigation" v-if="pagination.pages > 1" class="mt-4">
      <div class="text-center mb-3 text-muted">
        {{ $t('product.page') }} {{ pagination.currentPage }} {{ $t('product.of') }} {{ pagination.pages }}
      </div>
      <ul class="pagination justify-content-center">
        <li :class="['page-item', { disabled: pagination.currentPage === 1 }]">
          <a class="page-link" href="#" @click.prevent="changePage(pagination.currentPage - 1)">
            <i class="fas fa-chevron-left"></i>
          </a>
        </li>
        
        <!-- Show first page -->
        <li v-if="pagination.currentPage > 3" class="page-item">
          <a class="page-link" href="#" @click.prevent="changePage(1)">1</a>
        </li>
        <li v-if="pagination.currentPage > 4" class="page-item disabled">
          <span class="page-link">...</span>
        </li>
        
        <!-- Show current page and surrounding pages -->
        <li v-for="page_num in getVisiblePages()" :key="page_num" :class="['page-item', { active: page_num === pagination.currentPage }]">
          <a class="page-link" href="#" @click.prevent="changePage(page_num)">{{ page_num }}</a>
        </li>
        
        <!-- Show last pages -->
        <li v-if="pagination.currentPage < pagination.pages - 3" class="page-item disabled">
          <span class="page-link">...</span>
        </li>
        <li v-if="pagination.currentPage < pagination.pages - 2" class="page-item">
          <a class="page-link" href="#" @click.prevent="changePage(pagination.pages)">{{ pagination.pages }}</a>
        </li>
        
        <li :class="['page-item', { disabled: pagination.currentPage === pagination.pages }]">
          <a class="page-link" href="#" @click.prevent="changePage(pagination.currentPage + 1)">
            <i class="fas fa-chevron-right"></i>
          </a>
        </li>
      </ul>
    </nav>

    <!-- Product Details Modal -->
    <div class="modal fade" id="productDetailsModal" tabindex="-1" aria-labelledby="productDetailsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="productDetailsModalLabel">
              <i class="fas fa-eye me-2"></i>{{ $t('product.productDetails') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedProduct">
              <div class="row">
                <div class="col-md-4">
                  <img v-if="selectedProduct.image_url" :src="selectedProduct.image_url" class="img-fluid rounded" :alt="selectedProduct.name">
                  <div v-else class="bg-light rounded d-flex align-items-center justify-content-center" style="height: 200px;">
                    <i class="fas fa-image fa-3x text-muted"></i>
                  </div>
                </div>
                <div class="col-md-8">
                  <h4>{{ selectedProduct.name }}</h4>
                  <div class="mb-3">
                    <span class="badge me-1" :class="selectedProduct.category === 'DDR' ? 'bg-info' : selectedProduct.category === 'SSD' ? 'bg-success' : 'bg-secondary'">
                      {{ selectedProduct.category }}
                    </span>
                    <span class="badge bg-light text-dark">{{ selectedProduct.brand }}</span>
                  </div>
                  <div class="mb-3">
                    <strong>Model:</strong> {{ selectedProduct.model || 'N/A' }}
                  </div>
                  <div class="mb-3">
                    <strong>Description:</strong>
                    <p class="mt-2">{{ selectedProduct.description || $t('product.noDescriptionAvailable') }}</p>
                  </div>
                  <div class="mb-3">
                    <strong>ADATA Product:</strong>
                    <span :class="selectedProduct.is_adata_product ? 'text-success' : 'text-muted'">
                      {{ selectedProduct.is_adata_product ? $t('product.yes') : $t('product.no') }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('product.close') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Price History Modal -->
    <div class="modal fade" id="priceHistoryModal" tabindex="-1" aria-labelledby="priceHistoryModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="priceHistoryModalLabel">
              <i class="fas fa-chart-line me-2"></i>{{ $t('product.priceHistory') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedProductForHistory">
              <h6 class="mb-3">{{ selectedProductForHistory.name }}</h6>
              
              <!-- Current Prices -->
              <div class="mb-4">
                <h6>{{ $t('product.currentPrices') }}</h6>
                <div v-if="currentPrices && Object.keys(currentPrices).length > 0" class="price-info">
                  <div class="price-sources">
                    <div v-for="(priceInfo, source) in currentPrices" :key="source" class="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                      <span>
                        <i :class="getSourceIcon(source)" class="me-2"></i>{{ source }}
                      </span>
                      <div class="d-flex align-items-center gap-2">
                        <span class="fw-bold text-primary">{{ priceInfo.currency }} {{ formatPrice(priceInfo.price) }}</span>
                        <span v-if="priceInfo.discount_percentage" class="badge bg-warning">
                          -{{ priceInfo.discount_percentage.toFixed(0) }}%
                        </span>
                        <span v-if="priceInfo.availability" class="badge bg-info text-dark">
                          {{ priceInfo.availability }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-muted">{{ $t('product.noPriceDataAvailable') }}</div>
              </div>

              <!-- Price History Chart Component -->
              <div class="mb-4">
                <PriceHistoryChart 
                  v-if="selectedProductForHistory && selectedProductForHistory.id"
                  :product-id="selectedProductForHistory.id"
                  :auto-load="false"
                    :key="selectedProductForHistory.id"
                  ref="priceHistoryChart"
                  @data-loaded="onPriceHistoryLoaded"
                  @error="onPriceHistoryError"
                />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('product.close') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Reviews Modal -->
    <div class="modal fade" id="reviewsModal" tabindex="-1" aria-labelledby="reviewsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="reviewsModalLabel">
              <i class="fas fa-star me-2"></i>{{ $t('product.productReviews') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedProductForReviews">
              <h6 class="mb-3">{{ selectedProductForReviews.name }}</h6>
              
              <!-- Loading State -->
              <div v-if="reviewsLoading" class="text-center py-4">
                <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                <span class="text-muted">{{ $t('product.loadingReviewsData') }}</span>
              </div>
              
              <!-- Error State -->
              <div v-else-if="reviewsError" class="alert alert-warning">
                <i class="fas fa-exclamation-triangle me-2"></i>
                {{ $t('product.failedToLoadReviews') }}
              </div>
              
              <!-- Reviews Content -->
              <div v-else-if="reviewsData.product_id">
                <!-- Review Summary -->
                <div class="mb-4">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="d-flex align-items-center mb-2">
                        <span class="me-2">{{ $t('product.averageRating') }}</span>
                        <div class="d-flex align-items-center">
                          <div class="me-2">
                            <i v-for="i in 5" :key="i" 
                               :class="i <= (reviewsData.average_rating || 0) ? 'fas fa-star text-warning' : 'far fa-star text-muted'">
                            </i>
                          </div>
                          <span class="fw-bold">{{ (reviewsData.average_rating || 0).toFixed(1) }}/5</span>
                        </div>
                      </div>
                      <div>
                        <span class="text-muted">{{ $t('product.totalReviews') }}: {{ reviewsData.total_reviews || 0 }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Reviews List -->
                <div class="mb-4">
                  <h6>{{ $t('product.reviewsByPlatform') }}</h6>
                  <div v-if="reviewsData.reviews && reviewsData.reviews.length > 0">
                    <div v-for="review in reviewsData.reviews" :key="review.id" class="card mb-3">
                      <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                          <div class="d-flex align-items-center">
                            <i :class="getSourceIcon(review.source_platform)" class="me-2"></i>
                            <strong>{{ review.source_platform }}</strong>
                          </div>
                          <div class="d-flex align-items-center">
                            <div class="me-2">
                              <i v-for="i in 5" :key="i" 
                                 :class="i <= (review.rating || 0) ? 'fas fa-star text-warning' : 'far fa-star text-muted'"
                                 style="font-size: 0.8em;">
                              </i>
                            </div>
                            <span class="small text-muted">{{ (review.rating || 0).toFixed(1) }}</span>
                          </div>
                        </div>
                        
                        <div class="mb-2">
                          <span class="badge bg-info">{{ review.reviews_count }} {{ $t('product.reviewsCount') }}</span>
                        </div>
                        
                        <div v-if="review.top_review_content" class="mb-2">
                          <p class="mb-0 small text-muted">{{ $t('product.topReview') }}</p>
                          <p class="mb-0">{{ review.top_review_content }}</p>
                        </div>
                        
                        <div class="small text-muted">
                          <i class="fas fa-clock me-1"></i>
                          {{ formatDate(review.recorded_at) }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="bg-light rounded p-4 text-center">
                    <i class="fas fa-comments fa-3x text-muted mb-3"></i>
                    <p class="text-muted">{{ $t('product.noReviewsAvailable') }}</p>
                  </div>
                </div>
              </div>
              
              <!-- No Data State -->
              <div v-else class="bg-light rounded p-4 text-center">
                <i class="fas fa-comments fa-3x text-muted mb-3"></i>
                <p class="text-muted">{{ $t('product.clickToLoadReviews') }}</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('product.close') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Comments Modal -->
    <div class="modal fade" id="editCommentsModal" tabindex="-1" aria-labelledby="editCommentsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editCommentsModalLabel">
              <i class="fas fa-edit me-2"></i>{{ $t('product.editComments') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="editingProduct">
              <h6 class="mb-3">{{ editingProduct.name }}</h6>
              <div class="mb-3">
                <label for="productComments" class="form-label">{{ $t('product.comments') }}</label>
                <textarea class="form-control" id="productComments" rows="5" v-model="editedComment"></textarea>
              </div>
              <div v-if="commentError" class="alert alert-danger mt-3">
                <i class="fas fa-exclamation-triangle me-2"></i>{{ commentError }}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('product.cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="saveComment" :disabled="commentSaving">
              <span v-if="commentSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ commentSaving ? $t('product.saving') : $t('product.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- BSP Price Edit Modal -->
    <BspPriceEditModal
      :product="selectedProductForBspEdit"
      @bspPriceUpdated="handleBspPriceUpdatedFromList"
      ref="bspPriceEditModalRef"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import apiClient from '../api';
import { useRouter, useRoute } from 'vue-router';
import { Modal } from 'bootstrap';
import PriceHistoryChart from './PriceHistoryChart.vue';
import { getChannelIcon } from '../config/priceHistory';
import { formatBoughtCount } from '../utils/formatters';
import BspPriceEditModal from './BspPriceEditModal.vue'; // New import

const products = ref([]);
const pagination = ref({ total: 0, pages: 0, currentPage: 1, perPage: 12 });
const selectedCategory = ref('');
const searchQuery = ref('');
const loading = ref(true);
const error = ref(null);
const router = useRouter();
const route = useRoute();

const activeFilter = ref('all');
const selectedProduct = ref(null);
const selectedProductForHistory = ref(null);
const selectedProductForReviews = ref(null);
const currentPrices = ref({});
const priceHistoryChart = ref(null);

// 使用 price history composable


const currentSortBy = ref('product_name'); // Default sort by product_name
const currentOrder = ref('asc'); // Default sort order ascending

const editingProduct = ref(null);
const editedComment = ref('');
const commentSaving = ref(false);
const commentError = ref(null);

const selectedProductForBspEdit = ref(null); // New ref
const bspPriceEditModalRef = ref(null); // New ref

const formatPrice = (price) => {
  return price ? price.toFixed(2) : 'N/A';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

// Action methods
const handleTitleClick = (product) => {
  // Optional: Add analytics tracking or other logic here
  console.log('Opening product URL:', product.url);
};

const showProductDetails = (product) => {
  selectedProduct.value = product;
  // Show modal using Bootstrap's modal API
  const modal = new Modal(document.getElementById('productDetailsModal'));
  modal.show();
};

const showPriceHistory = async (product) => {
  selectedProduct.value = product;
  selectedProductForHistory.value = product;
  currentPrices.value = {};
  
  // Show modal using Bootstrap's modal API
  const modal = new Modal(document.getElementById('priceHistoryModal'));
  modal.show();
  
  // Data loading will be handled by PriceHistoryChart's mounted hook due to :key prop
  await nextTick(); // Ensure modal is rendered before chart component mounts
};

const reviewsData = ref({});
const reviewsLoading = ref(false);
const reviewsError = ref(null);

const showReviews = async (product) => {
  selectedProductForReviews.value = product;
  reviewsData.value = {};
  reviewsLoading.value = true;
  reviewsError.value = null;
  
  // Show modal using Bootstrap's modal API
  const modal = new Modal(document.getElementById('reviewsModal'));
  modal.show();
  
  // Fetch reviews data
  try {
    const response = await apiClient.get(`/products/${product.id}/reviews`, {
      params: { page: 1, page_size: 10 }
    });
    
    reviewsData.value = response.data;
    console.log('Reviews loaded:', reviewsData.value);
  } catch (error) {
    reviewsError.value = error.message || 'Failed to load reviews';
    console.error('Failed to fetch reviews:', error);
  } finally {
    reviewsLoading.value = false;
  }
};

// 價格歷史記錄相關事件處理
const onPriceHistoryLoaded = (data) => {
  console.log('Price history loaded:', data);
  
  // 從 PriceHistoryChart 組件獲取當前價格數據
  if (data && data.current_prices) {
    currentPrices.value = data.current_prices;
    console.log('Current prices loaded from chart:', currentPrices.value);
  }
};

const onPriceHistoryError = (error) => {
  console.error('Price history error:', error);
};

// 獲取來源圖示
const getSourceIcon = (source) => {
  return getChannelIcon(source);
};

const showEditCommentsModal = (product) => {
  editingProduct.value = product;
  editedComment.value = product.comments || '';
  commentError.value = null;
  const modal = new Modal(document.getElementById('editCommentsModal'));
  modal.show();
};

const saveComment = async () => {
  if (!editingProduct.value) return;

  commentSaving.value = true;
  commentError.value = null;

  try {
    const payload = { comments: editedComment.value };
    await apiClient.patch(`/products/${editingProduct.value.id}`, payload);

    // Update the product in the local products array
    const index = products.value.findIndex(p => p.id === editingProduct.value.id);
    if (index !== -1) {
      products.value[index].comments = editedComment.value;
    }

    // Close modal
    const modal = Modal.getInstance(document.getElementById('editCommentsModal'));
    if (modal) modal.hide();

  } catch (error) {
    commentError.value = error.response?.data?.detail || error.message || 'Failed to save comment';
    console.error('Failed to save comment:', error);
  } finally {
    commentSaving.value = false;
  }
};

const editBspPrice = (product) => {
  console.log('Attempting to edit BSP price for product:', product.id);
  selectedProductForBspEdit.value = product; // Set the product for the modal
  if (bspPriceEditModalRef.value) {
    bspPriceEditModalRef.value.openModal(); // Open the modal
  }
};

const handleBspPriceUpdatedFromList = ({ productId, newBspPrice }) => {
  // Find the product in the list and update its bsp_price
  const index = products.value.findIndex(p => p.id === productId);
  if (index !== -1) {
    products.value[index].bsp_price = newBspPrice;
  }
};

const setFilter = (filter) => {
  activeFilter.value = filter;
  selectedCategory.value = filter === 'all' ? '' : filter.toUpperCase();
  pagination.value.currentPage = 1;
  updateUrl();
};

const setSortBy = (sortByField) => {
  let newSortBy = sortByField;
  let newOrder = 'asc';

  // Toggle order if sorting by the same field
  if (currentSortBy.value === sortByField) {
    newOrder = currentOrder.value === 'asc' ? 'desc' : 'asc';
  }

  currentSortBy.value = newSortBy;
  currentOrder.value = newOrder;
  pagination.value.currentPage = 1; // Reset to first page on sort change
  updateUrl();
};

const getVisiblePages = () => {
  const current = pagination.value.currentPage;
  const total = pagination.value.pages;
  const pages = [];
  
  const start = Math.max(1, current - 2);
  const end = Math.min(total, current + 2);
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return pages;
};

const fetchProducts = async () => {
  loading.value = true;
  error.value = null;
  try {
    const params = {
      page: pagination.value.currentPage,
      page_size: pagination.value.perPage,
      search: searchQuery.value || undefined,
      sort_by: currentSortBy.value,
      order: currentOrder.value
    };
    
    // Map filter to category
    if (selectedCategory.value) {
      if (selectedCategory.value === 'DDR') {
        params.category = 'ddr_memory';
      } else if (selectedCategory.value === 'SSD') {
        params.category = 'ssd_storage';
      }
    }
    
    const response = await apiClient.get('/products', { params });

    // Transform the response data to match our component expectations
    products.value = response.data.data.map(product => ({
      id: product.id,
      name: product.product_name,
      model: product.product_model,
      brand: product.brand,
      category: product.category === 'ddr_memory' ? 'DDR' : product.category === 'ssd_storage' ? 'SSD' : product.category,
      is_adata_product: product.is_adata_product,
      description: product.description,
      image_url: product.image_url,
      url: product.url, // Add URL field from API response
      price: product.price,
      currency: product.currency,
      price_trend: product.price_trend,
      last_updated: product.last_updated,
      review_count: product.review_count || 0,
      average_rating: product.average_rating || 0,
      created_at: product.created_at,
      updated_at: product.updated_at,
      comments: product.comments || '',
      bought_past_month: product.bought_past_month,
      bsp_price: product.bsp_price
    }));

    pagination.value = {
      total: response.data.pagination.total_items,
      pages: response.data.pagination.total_pages,
      currentPage: response.data.pagination.current_page,
      perPage: response.data.pagination.page_size,
      hasNext: response.data.pagination.has_next,
      hasPrevious: response.data.pagination.has_previous,
    };
    
  } catch (err) {
    error.value = err.message || 'Failed to fetch products. Please check your connection.';
    console.error('Products fetch error:', err);
  } finally {
    loading.value = false;
  }
};

const updateUrl = () => {
    router.push({ 
        query: { 
            page: pagination.value.currentPage, 
            category: selectedCategory.value || undefined, 
            search: searchQuery.value || undefined,
            sort_by: currentSortBy.value,
            order: currentOrder.value
        }
    });
};

const changePage = (page) => {
  if (page > 0 && page <= pagination.value.pages) {
    pagination.value.currentPage = page;
    updateUrl();
  }
};

// const selectCategory = (category) => {
//   selectedCategory.value = category;
//   pagination.value.currentPage = 1; // Reset to first page
//   updateUrl();
// };

const handleSearch = () => {
    // This will trigger the watcher
    pagination.value.currentPage = 1;
    updateUrl();
};

// Watch for route query changes to fetch data
watch(() => route.query, (newQuery) => {
    pagination.value.currentPage = parseInt(newQuery.page) || 1;
    selectedCategory.value = newQuery.category || '';
    searchQuery.value = newQuery.search || '';
    fetchProducts();
}, { immediate: true });

onMounted(() => {
    // Initial fetch is handled by the watcher with immediate: true
});

</script>

<style scoped>
.card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.btn.active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.opacity-50 {
  opacity: 0.5;
}

.dropdown-item { 
  cursor: pointer; 
}

.card-title a:hover {
  color: #007bff !important;
}

/* Product title styling */
.product-title {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  height: 4.2em; /* 3 lines × 1.4 line-height = 4.2em */
  word-wrap: break-word;
  transition: color 0.2s ease-in-out;
}

.product-title:hover {
  color: #007bff !important;
}

.product-title .fa-external-link-alt {
  opacity: 0.6;
  transition: opacity 0.2s ease-in-out;
}

.product-title:hover .fa-external-link-alt {
  opacity: 1;
}

.card-title {
  min-height: 5rem; /* 確保標題區域有固定高度 */
}

/* Price information styling */
.price-info {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 0.75rem;
  border-left: 3px solid #007bff;
}

.price-sources {
  font-size: 0.875rem;
}

.price-sources .small {
  color: #6c757d;
}

/* Action buttons styling */
.btn-sm {
  font-size: 0.8rem;
  padding: 0.375rem 0.5rem;
}

/* Source icons */
.fab.fa-amazon {
  color: #ff9900;
}

.fas.fa-store {
  color: #0066cc;
}

.fas.fa-laptop {
  color: #ff6600;
}

.fas.fa-camera {
  color: #000;
}

.fas.fa-shopping-cart {
  color: #004c91;
}

.fas.fa-bullseye {
  color: #cc0000;
}

/* Notification styling */
.alert.position-fixed {
  animation: slideInRight 0.3s ease-out;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Modal styling */
.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.modal-title {
  color: #495057;
  font-weight: 600;
}

.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

/* Product details modal */
.modal-body img {
  max-height: 200px;
  object-fit: cover;
}

/* Price history modal */
.price-info .price-sources .bg-light {
  border: 1px solid #e9ecef;
}

/* Reviews modal */
.fa-star.text-warning {
  color: #ffc107 !important;
}

.fa-star.text-muted {
  color: #6c757d !important;
}
</style>